import {AppName} from '../types';

import {KeyboardShortcutCategories} from './types';

/**
 * Returns a lab's shortcut listing, but only when it has at least one
 * category. Callers gate on the truthiness of the result, so an entry with no
 * categories is treated the same as no entry at all.
 */
export function getLabShortcuts(
  appName: AppName
): KeyboardShortcutCategories | undefined {
  const shortcuts = ShortcutsPerLab[appName];
  return shortcuts && Object.keys(shortcuts).length > 0 ? shortcuts : undefined;
}

/**
 * Keyboard shortcuts to surface for each lab, keyed by app name.
 * A lab with an entry here gets the Resources-tab hint and the
 * `/`-triggered shortcuts popover automatically. The bindings themselves live
 * in each lab's own key handling; this is only the human-readable listing.
 */
export const ShortcutsPerLab: Partial<
  Record<AppName, KeyboardShortcutCategories>
> = {
  sketchlab: {
    Navigation: [
      {shortcut: 'Tab', explanation: 'Move focus between elements (forward)'},
      {
        shortcut: 'Shift + Tab',
        explanation: 'Move focus between elements (backward)',
      },
    ],
    'Canvas tools': [
      {shortcut: 'S', explanation: 'Switch to the select tool'},
      {shortcut: 'H', explanation: 'Switch to the hand tool'},
    ],
    Editing: [
      {shortcut: 'Enter', explanation: 'Edit the focused element'},
      {shortcut: 'E', explanation: 'Open the element toolbar'},
      {shortcut: 'Esc', explanation: 'Finish editing'},
    ],
    Connecting: [
      {
        shortcut: 'C',
        explanation:
          'Start a connection. If already started, connect to the focused element',
      },
      {shortcut: 'Enter', explanation: 'Connect to the focused element'},
      {shortcut: 'Esc', explanation: 'Cancel connection'},
    ],
    Grouping: [
      {shortcut: 'G', explanation: 'Enter group mode or create the group'},
      {shortcut: 'Enter', explanation: 'Add or remove the focused element'},
      {shortcut: 'Esc', explanation: 'Cancel group mode'},
    ],
    'Move & resize': [
      {shortcut: 'Arrow keys', explanation: 'Move the focused element'},
      {shortcut: '[ or ]', explanation: 'Shrink or grow the focused shape'},
      {shortcut: 'Shift + [ or ]', explanation: 'Resize width only'},
      {shortcut: 'Alt / Opt + [ or ]', explanation: 'Resize height only'},
    ],
    'Clipboard & history': [
      {shortcut: 'Ctrl / Cmd + C', explanation: 'Copy'},
      {shortcut: 'Ctrl / Cmd + X', explanation: 'Cut'},
      {shortcut: 'Ctrl / Cmd + V', explanation: 'Paste'},
      {
        shortcut: 'Delete / Backspace',
        explanation: 'Delete the selected element',
      },
      {shortcut: 'Ctrl / Cmd + Z', explanation: 'Undo'},
      {shortcut: 'Ctrl / Cmd + Y', explanation: 'Redo'},
    ],
  },
};
